import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, CircularProgress, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
  ArrowBack, Warehouse as WarehouseIcon, Inventory2, CheckCircleOutline, Lock,
  PauseCircleOutlined, Category, LocalShipping,
} from '@mui/icons-material';
import inventoryCommandService from '../../services/inventoryCommandService';
import { formatCurrency, formatIndianNumber } from '../../utils/currency';
import {
  PageHeader, StatCard, TableSurface, headerCellSx, bodyRowSx, StatusDot, StatusTone,
  ErrorState, TableEmptyState,
} from '../../components/common/pageKit';

// ---------------------------------------------------------------------------
// WAREHOUSE 360 — a single warehouse's stock value, availability, top items,
// bin occupancy and recent movement. Backed by GET
// /inventory-command/warehouse/:warehouseId/360. As with Item 360, nested
// array field names aren't fully pinned in the contract, so accessors try a
// short list of plausible aliases and fall back to an em dash.
// ---------------------------------------------------------------------------

const TABS = ['Top Items', 'Bins', 'Movements'];

const pick = (row: any, keys: string[]): any => {
  if (!row) return undefined;
  for (const k of keys) if (row[k] !== undefined && row[k] !== null) return row[k];
  return undefined;
};

const fmtQty = (v: any): string => (v === null || v === undefined || v === '' ? '—' : Number(v).toLocaleString('en-IN'));
const fmtDate = (v: any): string => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? String(v) : d.toLocaleDateString('en-IN');
};

const movementTone = (type: any): StatusTone => {
  const t = String(type || '').toUpperCase();
  if (t === 'IN' || t.includes('RECEIPT')) return 'success';
  if (t === 'OUT' || t.includes('ISSUE') || t.includes('DISPATCH')) return 'warning';
  return 'neutral';
};

const WarehouseCommandPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [d, setD] = useState<any>(null);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    inventoryCommandService.warehouse360(Number(id))
      .then(setD)
      .catch((e: any) => setError(e?.message || 'Failed to load warehouse 360.'))
      .finally(() => setLoading(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (loading && !d) return <Box sx={{ display: 'grid', placeItems: 'center', height: '60vh' }}><CircularProgress /></Box>;
  if (error && !d) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/inventory')} sx={{ mb: 1 }}>Back to Inventory</Button>
        <TableSurface><ErrorState message={error} onRetry={load} /></TableSurface>
      </Box>
    );
  }
  if (!d) return null;

  const h = d.header || {};
  const topItems: any[] = d.topItems || [];
  const bins: any[] = d.bins || [];
  const movements: any[] = d.movements || [];

  const name = pick(h, ['name', 'warehouseName']);
  const code = pick(h, ['code', 'warehouseCode']);
  const location = pick(h, ['location', 'city', 'address']);
  const isActive = pick(h, ['isActive']);

  const subtitleBits = [location || null].filter(Boolean);

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/inventory')} sx={{ mb: 1 }}>Back to Inventory</Button>

      <PageHeader
        title={name || '—'}
        qualifier={code ? `· ${code}` : undefined}
        subtitle={subtitleBits.join('  ·  ')}
        actions={
          <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', flexWrap: 'wrap' }}>
            {isActive !== undefined && <StatusDot label={isActive ? 'Active' : 'Inactive'} tone={isActive ? 'success' : 'neutral'} />}
          </Box>
        }
      />

      {/* KPI row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 2, mb: 2.5 }}>
        <StatCard title="Stock Value" value={d.stockValue != null ? formatIndianNumber(Number(d.stockValue)) : null} icon={<Inventory2 />} color="#2563eb" />
        <StatCard title="Available" value={d.availableValue != null ? formatIndianNumber(Number(d.availableValue)) : null} icon={<CheckCircleOutline />} color="#16a34a" />
        <StatCard title="Reserved" value={d.reservedValue != null ? formatIndianNumber(Number(d.reservedValue)) : null} icon={<Lock />} color="#d97706" />
        <StatCard title="QC Hold" value={d.qcHoldValue != null ? formatIndianNumber(Number(d.qcHoldValue)) : null} icon={<PauseCircleOutlined />} color="#7c3aed" />
        <StatCard title="Item Count" value={d.itemCount != null ? fmtQty(d.itemCount) : null} icon={<Category />} color="#0891b2" />
        <StatCard title="Pending Receipts" value={d.pendingReceipts != null ? fmtQty(d.pendingReceipts) : null} icon={<LocalShipping />} color="#059669" />
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, minHeight: 40, borderBottom: '1px solid', borderColor: 'divider', '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 700 } }}
      >
        {TABS.map((t) => <Tab key={t} label={t} />)}
      </Tabs>

      {/* Top Items */}
      {tab === 0 && (
        <TableSurface>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Code</TableCell>
                  <TableCell sx={headerCellSx}>Product</TableCell>
                  <TableCell sx={headerCellSx} align="right">Qty</TableCell>
                  <TableCell sx={headerCellSx} align="right">Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topItems.map((it: any, i: number) => {
                  const productId = pick(it, ['productId', 'id']);
                  return (
                    <TableRow
                      key={productId ?? i}
                      hover
                      sx={{ ...(bodyRowSx as any), cursor: productId ? 'pointer' : 'default' }}
                      onClick={() => productId && navigate(`/inventory/item/${productId}`)}
                    >
                      <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{pick(it, ['code', 'productCode']) || '—'}</TableCell>
                      <TableCell>{pick(it, ['product', 'name', 'productName']) || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtQty(pick(it, ['qty', 'stock', 'onHand']))}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{pick(it, ['value', 'stockValue']) != null ? formatCurrency(Number(pick(it, ['value', 'stockValue']))) : '—'}</TableCell>
                    </TableRow>
                  );
                })}
                {!topItems.length && <TableRow><TableCell colSpan={4} sx={{ border: 0 }}><TableEmptyState title="No stock items" hint="This warehouse has no valued stock." /></TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </TableSurface>
      )}

      {/* Bins */}
      {tab === 1 && (
        <TableSurface>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Bin</TableCell>
                  <TableCell sx={headerCellSx}>Product</TableCell>
                  <TableCell sx={headerCellSx} align="right">Qty</TableCell>
                  <TableCell sx={headerCellSx} align="right">Capacity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bins.map((b: any, i: number) => {
                  const qty = pick(b, ['qty', 'quantity', 'occupied']);
                  const capacity = pick(b, ['capacity', 'maxQty']);
                  const util = qty != null && capacity ? Math.min((Number(qty) / Number(capacity)) * 100, 100) : null;
                  return (
                    <TableRow key={i} hover sx={bodyRowSx as any}>
                      <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontFamily: 'monospace', fontWeight: 700 }}>
                        <WarehouseIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
                        {pick(b, ['binCode', 'code', 'bin']) || '—'}
                      </TableCell>
                      <TableCell>{pick(b, ['product', 'productName']) || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtQty(qty)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {capacity != null ? `${fmtQty(capacity)}${util != null ? ` (${util.toFixed(0)}%)` : ''}` : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!bins.length && <TableRow><TableCell colSpan={4} sx={{ border: 0 }}><TableEmptyState title="No bins configured" hint="This warehouse has no bin locations." /></TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </TableSurface>
      )}

      {/* Movements */}
      {tab === 2 && (
        <TableSurface>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Date</TableCell>
                  <TableCell sx={headerCellSx}>Type</TableCell>
                  <TableCell sx={headerCellSx}>Product</TableCell>
                  <TableCell sx={headerCellSx} align="right">Qty</TableCell>
                  <TableCell sx={headerCellSx}>Reference</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {movements.map((m: any, i: number) => {
                  const type = pick(m, ['type', 'movementType']);
                  const qty = pick(m, ['qty', 'quantity']);
                  const tone = movementTone(type);
                  const productId = pick(m, ['productId']);
                  return (
                    <TableRow
                      key={i}
                      hover
                      sx={{ ...(bodyRowSx as any), cursor: productId ? 'pointer' : 'default' }}
                      onClick={() => productId && navigate(`/inventory/item/${productId}`)}
                    >
                      <TableCell><Box component="span" sx={{ fontSize: 12 }}>{fmtDate(pick(m, ['date', 'movementDate', 'ts']))}</Box></TableCell>
                      <TableCell><StatusDot label={type || 'Unknown'} tone={tone} /></TableCell>
                      <TableCell>{pick(m, ['product', 'productName']) || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: tone === 'success' ? 'success.main' : tone === 'warning' ? 'warning.main' : 'inherit' }}>
                        {qty != null ? `${tone === 'success' ? '+' : tone === 'warning' ? '-' : ''}${fmtQty(qty)}` : '—'}
                      </TableCell>
                      <TableCell>{pick(m, ['reference', 'ref', 'referenceType']) || '—'}</TableCell>
                    </TableRow>
                  );
                })}
                {!movements.length && <TableRow><TableCell colSpan={5} sx={{ border: 0 }}><TableEmptyState title="No recent movements" hint="No stock transactions have touched this warehouse recently." /></TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </TableSurface>
      )}
    </Box>
  );
};

export default WarehouseCommandPage;
